import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import TextField from '@app/src/components/Ui/TextField'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import { getSolutionTypeLabel } from '@app/src/pages/Configurations/ConfigurationsPages/TemplateAdmin/availabilityHelpers'
import { getMrtTableOptions } from '@app/src/pages/ResourceCollection/MrtCollection/getMrtTableOptions'
import MrtTable from '@app/src/pages/ResourceCollection/MrtCollection/MrtTable'
import type { OrganizationEntitlementOverview } from '@app/src/types/entitlements'
import { EntitlementEffect, EntitlementFeature } from '@app/src/types/entitlements'
import type { Organization } from '@app/src/types/organizations'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Paper, Stack, Typography } from '@mui/material'
import type { MRT_ColumnDef, MRT_Row } from 'material-react-table'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import EditOrganizationCsmSettingsDrawer from '../EditOrganizationCsmSettingsDrawer'
import type { AccessCell, AccessRow } from './accessMatrix'
import {
  accessRows,
  accessSolutions,
  featureLabel,
  featureWideChange,
  findRule,
  grantAllChanges,
  templateGrantCount,
} from './accessMatrix'
import EditEntitlementDrawer from './EditEntitlementDrawer'
import EntitlementCell from './EntitlementCell'
import { useOrganizationEntitlements } from './useOrganizationEntitlements'

type Props = {
  organization: Organization
}

const accessColumns = (onEdit: (cell: AccessCell) => void): MRT_ColumnDef<AccessRow>[] => [
  {
    id: 'title',
    header: 'Template',
    accessorKey: 'title',
    size: 360,
    Cell: ({ row }: { row: MRT_Row<AccessRow> }) => (
      <Stack>
        <Typography variant="body2">{row.original.title}</Typography>
        {(row.original.isAssessment || row.original.isDefaultScope) && (
          <Typography variant="caption" color="text.secondary">
            {row.original.isAssessment ? 'Assessment' : 'Default template'}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    id: 'questionnaire',
    header: 'Send questionnaire',
    accessorFn: row => row.questionnaire?.status ?? '',
    Cell: ({ row }: { row: MRT_Row<AccessRow> }) => (
      <EntitlementCell cell={row.original.questionnaire} onEdit={onEdit} />
    ),
  },
  {
    id: 'aiScreening',
    header: 'AI screening',
    accessorFn: row => row.aiScreening?.status ?? '',
    Cell: ({ row }: { row: MRT_Row<AccessRow> }) => <EntitlementCell cell={row.original.aiScreening} onEdit={onEdit} />,
  },
  {
    id: 'assessment',
    header: 'Assessment',
    accessorFn: row => row.assessment?.status ?? '',
    Cell: ({ row }: { row: MRT_Row<AccessRow> }) => <EntitlementCell cell={row.original.assessment} onEdit={onEdit} />,
  },
]

const OrganizationAccessTab: React.FC<Props> = ({ organization }) => {
  const { formatMessage } = useIntl()
  const solutions = accessSolutions(organization.assignedSolutions?.map(solution => solution.solutionType) ?? [])
  const [solutionType, setSolutionType] = useState(solutions[0])
  const [onlyOrganizationRules, setOnlyOrganizationRules] = useState(false)
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const { openDrawer, closeDrawer } = useDrawer()
  const { showSnackbar } = useSnackbar()
  const { overview, isLoading, save, isSaving } = useOrganizationEntitlements(organization.id, solutionType)

  const editCell = useCallback(
    (cell: AccessCell) =>
      openDrawer(
        <EditEntitlementDrawer
          organizationId={organization.id}
          organizationName={organization.name}
          solutionType={solutionType}
          feature={cell.scope.feature}
          scopeId={cell.scope.scopeId}
          closeDrawer={closeDrawer}
        />,
      ),
    [openDrawer, closeDrawer, organization.id, organization.name, solutionType],
  )
  const columns = useMemo(() => accessColumns(editCell), [editCell])
  const rows = useMemo(
    () => (overview ? accessRows(overview, onlyOrganizationRules) : []),
    [overview, onlyOrganizationRules],
  )

  if (!solutionType)
    return <Typography color="text.secondary">This organization has no solution that access applies to.</Typography>

  const solutionSelector = solutions.length > 1 && (
    <TextField
      select
      label="Solution"
      value={solutionType}
      onChange={event => setSolutionType(solutions.find(solution => solution === event.target.value) ?? solutionType)}
      sx={{ minWidth: 200 }}
    >
      {solutions.map(solution => (
        <MenuItem key={solution} value={solution}>
          {getSolutionTypeLabel(solution, formatMessage)}
        </MenuItem>
      ))}
    </TextField>
  )

  if (isLoading) return <CircularProgress />

  if (!overview)
    return (
      <Stack spacing={2} alignItems="flex-start">
        {solutionSelector}
        <Typography color="error">Couldn&apos;t load access for this solution.</Typography>
      </Stack>
    )

  const grantAll = (feature: EntitlementFeature) => {
    const changes = grantAllChanges(overview.scopes, feature)
    if (changes.length === 0) {
      showSnackbar({
        message: `They already have ${featureLabel[feature]} on every visible template`,
        severity: NotificationSeverity.info,
      })
      return
    }
    save(changes, `Granted ${featureLabel[feature]} on ${changes.length} templates`)
  }

  const screeningTemplateGrants = templateGrantCount(overview, EntitlementFeature.AiScreening)
  const denyAllMessage = screeningTemplateGrants
    ? `AI screening denied, except ${screeningTemplateGrants} templates with their own grant`
    : 'All AI screening denied'
  const isScreeningDeniedForAll =
    findRule(overview.entitlements, EntitlementFeature.AiScreening)?.effect === EntitlementEffect.Deny

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="stretch" flexWrap="wrap" useFlexGap>
        {solutionSelector}
        <CreditsSummary
          overview={overview}
          onEdit={() =>
            openDrawer(
              <EditOrganizationCsmSettingsDrawer
                closeDrawer={closeDrawer}
                organization={organization}
                solutionType={solutionType}
              />,
            )
          }
        />
      </Stack>

      <MrtTable
        exitFullScreen={false}
        options={{
          ...getMrtTableOptions<AccessRow>({
            getRowId: row => row.rowId,
            tableContainerRef,
            getSubRows: row => row.subRows,
            enableRowSelection: false,
            rowSizing: 'content',
            scrollWithPage: true,
          }),
          columns,
          data: rows,
          initialState: { expanded: true, columnPinning: { left: ['title'] } },
          manualFiltering: false,
          manualSorting: false,
          manualPagination: false,
          enablePagination: false,
          enableBottomToolbar: false,
          enableFullScreenToggle: false,
          filterFromLeafRows: true,
          globalFilterFn: 'contains',
          renderTopToolbarCustomActions: () => (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyOrganizationRules}
                    onChange={event => setOnlyOrganizationRules(event.target.checked)}
                  />
                }
                label="Only rules for this organization"
              />
              <Button disabled={isSaving} onClick={() => grantAll(EntitlementFeature.Assessment)}>
                Grant all assessments
              </Button>
              <Button disabled={isSaving} onClick={() => grantAll(EntitlementFeature.AiScreening)}>
                Grant screening on all
              </Button>
              <Button
                disabled={isSaving}
                color={isScreeningDeniedForAll ? 'primary' : 'error'}
                onClick={() =>
                  save(
                    [
                      featureWideChange(
                        EntitlementFeature.AiScreening,
                        isScreeningDeniedForAll ? null : EntitlementEffect.Deny,
                      ),
                    ],
                    isScreeningDeniedForAll ? '“Deny all screening” removed' : denyAllMessage,
                  )
                }
              >
                {isScreeningDeniedForAll ? 'Remove “deny all screening”' : 'Deny all screening'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Changes apply to every solution this organization has.
              </Typography>
            </Stack>
          ),
        }}
      />
    </Stack>
  )
}

const CreditsSummary: React.FC<{ overview: OrganizationEntitlementOverview; onEdit: () => void }> = ({
  overview,
  onEdit,
}) => (
  <Paper variant="outlined" sx={{ px: 2, py: 1.5 }}>
    <Stack direction="row" spacing={3} alignItems="center">
      <Stack>
        <Typography variant="caption" color="text.secondary">
          AI screening credits
        </Typography>
        <Typography variant="subtitle1">
          {overview.remainingProviderScreenings ?? 0} of {overview.allowedProviderScreenings ?? 0} left
        </Typography>
      </Stack>
      <Button size="small" onClick={onEdit}>
        Edit limit
      </Button>
    </Stack>
  </Paper>
)

export default OrganizationAccessTab
